import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowRight, IconCheck, IconPencil } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from './primitives';
import { relationMeta } from './helpers';
import { UserBadge } from '../../../components/user/UserBadge';
import { PeerStatus, PeerView } from './types';

interface FeedbackBodyProps {
  peers: PeerView[];
  onOpenForm: (formId: string) => void;
  isMobile?: boolean;
}

export const FeedbackBody: React.FC<FeedbackBodyProps> = ({ peers, onOpenForm, isMobile }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | PeerStatus>('all');
  const [isListOpen, setIsListOpen] = useState(false);
  const counts = useMemo(
    () => ({
      all: peers.length,
      pending: peers.filter((p) => p.status === 'pending').length,
      draft: peers.filter((p) => p.status === 'draft').length,
      submitted: peers.filter((p) => p.status === 'submitted').length,
    }),
    [peers],
  );
  // Фильтровать нечего, пока все ревью в одном статусе — три пустые вкладки
  // только шумят.
  const hasMixedStatuses =
    [counts.pending, counts.draft, counts.submitted].filter((n) => n > 0).length > 1;
  // Переключатель мог исчезнуть уже после выбора — сдали последнее ревью, и
  // статус остался один. Выбранное «не начато» тогда показывало бы пустой
  // список при девяти сданных.
  const activeFilter = hasMixedStatuses ? filter : 'all';
  // Сначала то, что ещё должен сделать: не начато, потом черновики, потом
  // сданное. Иначе единственное незакрытое ревью тонет в конце списка.
  const order: Record<PeerStatus, number> = { pending: 0, draft: 1, submitted: 2 };
  const filtered = (activeFilter === 'all' ? peers : peers.filter((p) => p.status === activeFilter))
    .slice()
    .sort((a, b) => order[a.status] - order[b.status]);
  const isAllSubmitted = counts.all > 0 && counts.submitted === counts.all;

  return (
    <Paper withBorder p={isMobile ? 'md' : 'xl'} radius="md">
      <Flex
        justify="space-between"
        align="flex-end"
        mb={isAllSubmitted && !isListOpen ? 0 : 'lg'}
        gap="md"
        wrap="wrap"
      >
        <Box style={{ minWidth: 0 }}>
          <Eyebrow>{t('perfReview.home.feedback.section')}</Eyebrow>
          <Title
            order={3}
            size="h4"
            mt={6}
            style={{
              letterSpacing: '-0.01em',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
            }}
          >
            {t('perfReview.home.feedback.assignedCount', { count: peers.length })}
          </Title>
        </Box>
        {isAllSubmitted && (
          <Group gap="xs" wrap="nowrap">
            <Badge color="teal" variant="light" size="sm" leftSection={<IconCheck size={12} />}>
              {t('perfReview.home.feedback.allSubmitted')}
            </Badge>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => setIsListOpen((v) => !v)}
            >
              {isListOpen
                ? t('perfReview.home.feedback.hideList')
                : t('perfReview.home.feedback.showList')}
            </Button>
          </Group>
        )}
        {hasMixedStatuses && (
          <SegmentedControl
            size="xs"
            fullWidth={isMobile}
            value={filter}
            onChange={(v) => setFilter(v as 'all' | PeerStatus)}
            data={[
              {
                value: 'all',
                label: t('perfReview.home.feedback.filterAll', { count: counts.all }),
              },
              {
                value: 'pending',
                label: t('perfReview.home.feedback.filterPending', { count: counts.pending }),
              },
              {
                value: 'draft',
                label: t('perfReview.home.feedback.filterDraft', { count: counts.draft }),
              },
              {
                value: 'submitted',
                label: t('perfReview.home.feedback.filterSubmitted', { count: counts.submitted }),
              },
            ]}
            style={{ width: isMobile ? '100%' : undefined }}
          />
        )}
      </Flex>

      {isAllSubmitted && !isListOpen ? null : filtered.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          {t('perfReview.home.feedback.empty')}
        </Text>
      ) : (
        <Stack gap="xs">
          {filtered.map((p) => (
            <PeerRow key={p.id} peer={p} onOpen={() => onOpenForm(p.id)} isMobile={isMobile} />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

const PeerRow: React.FC<{ peer: PeerView; onOpen: () => void; isMobile?: boolean }> = ({
  peer,
  onOpen,
  isMobile,
}) => {
  const { t } = useTranslation();
  const rel = relationMeta[peer.relation];
  const statusBadge =
    peer.status === 'submitted' ? (
      <Badge color="teal" variant="light" size="sm" leftSection={<IconCheck size={12} />}>
        {t('perfReview.home.feedback.statusSubmitted')}
      </Badge>
    ) : peer.status === 'draft' ? (
      <Badge color="orange" variant="light" size="sm" leftSection={<IconPencil size={12} />}>
        {t('perfReview.home.feedback.statusDraft')}
      </Badge>
    ) : (
      <Badge variant="default" size="sm">
        {t('perfReview.home.feedback.statusPending')}
      </Badge>
    );
  const actionBtn =
    peer.status === 'submitted' ? (
      <Button
        size="xs"
        variant="subtle"
        color="teal"
        rightSection={<IconArrowRight size={14} />}
        onClick={onOpen}
      >
        {t('perfReview.home.feedback.actionView')}
      </Button>
    ) : peer.status === 'draft' ? (
      <Button size="xs" color="teal" rightSection={<IconArrowRight size={14} />} onClick={onOpen}>
        {t('perfReview.home.feedback.actionContinue')}
      </Button>
    ) : (
      <Button
        size="xs"
        variant="default"
        rightSection={<IconArrowRight size={14} />}
        onClick={onOpen}
      >
        {t('perfReview.home.feedback.actionStart')}
      </Button>
    );

  const relationChip = (
    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
      <Box
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: `var(--mantine-color-${rel.color}-6)`,
        }}
      />
      <Text size="xs" c={rel.color} fw={500}>
        {t(rel.i18nKey)}
      </Text>
    </Group>
  );

  const subtitle = [peer.position, peer.team].filter(Boolean).join(' · ') || null;
  const badge = (
    <UserBadge
      user={{ name: peer.name || '—', avatar: peer.avatar }}
      size={40}
      subtitle={subtitle}
    />
  );

  if (isMobile) {
    return (
      <Paper withBorder p="sm" radius="md">
        <Stack gap={6}>
          {badge}
          <Flex gap="xs" align="center" wrap="wrap">
            {relationChip}
            {statusBadge}
          </Flex>
          <Flex justify="flex-end">{actionBtn}</Flex>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="sm" radius="md">
      <Flex gap="md" align="center" wrap="nowrap">
        <Box style={{ flex: 1, minWidth: 0 }}>{badge}</Box>
        {relationChip}
        <Box style={{ flexShrink: 0 }}>{statusBadge}</Box>
        <Box
          style={{
            flexShrink: 0,
            minWidth: 130,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {actionBtn}
        </Box>
      </Flex>
    </Paper>
  );
};
